import React, { useState, useEffect, useRef, useCallback } from "react";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Button } from "../../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { RichTextEditor } from "../RichTextEditor";
import {
  InternalControlData,
  FREQUENCY_OPTIONS,
  NATURE_OF_PROCEDURE_OPTIONS,
} from "../types";
import { Plus, Trash2, Copy } from "lucide-react";
import { ControlDocumentManager } from "../ControlDocumentManager";

interface OperatingEffectivenessProps {
  data: InternalControlData;
  onChange: (data: Partial<InternalControlData>) => void;
  controlId?: string;
}

// Separate memoized component for operating step items to prevent re-renders
const OperatingStepItem = React.memo<{
  step: typeof InternalControlData.prototype.operatingSteps[0];
  index: number;
  onUpdate: (id: string, field: string, value: string) => void;
  onRemove: (id: string) => void;
}>(({ step, index, onUpdate, onRemove }) => {
  // Use refs to ensure callbacks are stable
  const stepIdRef = React.useRef(step.id);
  const onUpdateRef = React.useRef(onUpdate);
  const onRemoveRef = React.useRef(onRemove);
  
  React.useEffect(() => {
    stepIdRef.current = step.id;
    onUpdateRef.current = onUpdate;
    onRemoveRef.current = onRemove;
  }, [step.id, onUpdate, onRemove]);

  const handleNatureChange = React.useCallback(
    (value: string) => onUpdateRef.current(stepIdRef.current, "natureOfProcedure", value),
    []
  );
  
  const handleProcedureChange = React.useCallback(
    (content: string) => onUpdateRef.current(stepIdRef.current, "procedureDescription", content),
    []
  );

  const handleRemove = React.useCallback(
    () => onRemoveRef.current(stepIdRef.current),
    []
  );

  // Memoize the Select value to prevent unnecessary re-renders
  const selectValue = React.useMemo(() => step.natureOfProcedure || "", [step.natureOfProcedure]);
  const editorValue = React.useMemo(() => step.procedureDescription, [step.procedureDescription]);

  return (
    <div className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-white">
          Procedure {index + 2}
        </h4>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRemove}
          className="text-red-400 hover:text-red-300"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2">
        <Label className="text-white">Nature of testing procedure</Label>
        <Select
          value={selectValue}
          onValueChange={handleNatureChange}
          searchable={false}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select nature of procedure" />
          </SelectTrigger>
          <SelectContent className="bg-black/90 border-white/10" searchable={false}>
            {natureOfProcedureItems}
          </SelectContent>
        </Select>
      </div>

      <RichTextEditor
        label="Procedure Description"
        value={editorValue}
        onChange={handleProcedureChange}
        placeholder="Describe the procedure..."
      />
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: only re-render if step data actually changed
  return (
    prevProps.step.id === nextProps.step.id &&
    prevProps.step.natureOfProcedure === nextProps.step.natureOfProcedure &&
    prevProps.step.procedureDescription === nextProps.step.procedureDescription &&
    prevProps.index === nextProps.index &&
    prevProps.onUpdate === nextProps.onUpdate &&
    prevProps.onRemove === nextProps.onRemove
  );
});
OperatingStepItem.displayName = "OperatingStepItem";

export const OperatingEffectiveness: React.FC<OperatingEffectivenessProps> = React.memo(({
  data,
  onChange,
  controlId,
}) => {
  const [riskOfMaterialMisstatement, setRiskOfMaterialMisstatement] = useState<string>("");
  const [loadingRiskAssessment, setLoadingRiskAssessment] = useState(false);
  const lastLoadedControlIdRef = useRef<string | undefined>(undefined);
  const isLoadingRef = useRef(false);
  // Use ref to access latest data without causing callback recreation
  const dataRef = useRef(data);
  const onChangeRef = useRef(onChange);
  
  useEffect(() => {
    dataRef.current = data;
    onChangeRef.current = onChange;
  }, [data, onChange]);

  // Stabilize callbacks for ControlDocumentManager to prevent infinite loops
  const handleTimingDocumentsChange = useCallback((documents: typeof data.timingDocuments) => {
    onChangeRef.current({ timingDocuments: documents });
  }, []);

  const handleTimingTextResponseChange = useCallback((textResponse: typeof data.timingTextResponse) => {
    onChangeRef.current({ timingTextResponse: textResponse });
  }, []);

  const handleOperatingTestsDocumentsChange = useCallback((documents: typeof data.operatingTestsDocuments) => {
    onChangeRef.current({ operatingTestsDocuments: documents });
  }, []);

  const handleOperatingTestsTextResponseChange = useCallback((textResponse: typeof data.operatingTestsTextResponse) => {
    onChangeRef.current({ operatingTestsTextResponse: textResponse });
  }, []);

  // Load Risk of Material Misstatement from ROMM assessments
  useEffect(() => {
    const loadRiskAssessment = async () => {
      if (!controlId) {
        setRiskOfMaterialMisstatement("No Risks Associated");
        lastLoadedControlIdRef.current = undefined;
        return;
      }

      // Prevent duplicate loads for the same controlId
      if (lastLoadedControlIdRef.current === controlId && !isLoadingRef.current) {
        return;
      }

      // Prevent concurrent loads
      if (isLoadingRef.current) {
        return;
      }

      lastLoadedControlIdRef.current = controlId;
      isLoadingRef.current = true;
      setLoadingRiskAssessment(true);

      try {
        // Step 1: Load Libraries_RCM.json to get ROMM IDs for this control
        let rcmData: any = {};
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadRcmJson === "function") {
          const rcmResult = await window.sharePointAPI.downloadRcmJson();
          if (rcmResult.success && rcmResult.data) {
            rcmData = rcmResult.data;
          } else {
            console.error("Failed to load Libraries_RCM.json:", rcmResult.error);
            setRiskOfMaterialMisstatement("No Risks Associated");
            isLoadingRef.current = false;
            setLoadingRiskAssessment(false);
            return;
          }
        } else {
          setRiskOfMaterialMisstatement("No Risks Associated");
          isLoadingRef.current = false;
          setLoadingRiskAssessment(false);
          return;
        }

        // Get ROMM IDs for this control
        const librariesRcm = rcmData.libraries_RCM || rcmData.libraries_rcm || {};
        const controlData = librariesRcm[controlId];
        
        if (!controlData || !controlData.romm || !Array.isArray(controlData.romm) || controlData.romm.length === 0) {
          setRiskOfMaterialMisstatement("No Risks Associated");
          isLoadingRef.current = false;
          setLoadingRiskAssessment(false);
          return;
        }

        const rommIds = controlData.romm;

        // Step 2: Load Libraries_Romm.json to get assessments
        let rommData: any = {};
        if (window.sharePointAPI && typeof window.sharePointAPI.readRommLibrary === "function") {
          const rommResult = await window.sharePointAPI.readRommLibrary();
          if (rommResult.success && rommResult.data) {
            rommData = rommResult.data;
          } else {
            console.error("Failed to load Libraries_Romm.json:", rommResult.error);
            setRiskOfMaterialMisstatement("No Risks Associated");
            isLoadingRef.current = false;
            setLoadingRiskAssessment(false);
            return;
          }
        } else {
          setRiskOfMaterialMisstatement("No Risks Associated");
          isLoadingRef.current = false;
          setLoadingRiskAssessment(false);
          return;
        }

        // Get assessments for each ROMM ID
        const rommLibrary = rommData.romm_library || [];
        const assessments: string[] = [];

        for (const rommId of rommIds) {
          const rommEntry = rommLibrary.find((r: any) => (r["romm-id"] || "") === rommId);
          if (rommEntry && rommEntry.assesment) {
            assessments.push(rommEntry.assesment);
          }
        }

        // Determine highest priority assessment: Significant > Higher > Lower > NRPMM
        let finalAssessment = "No Risks Associated";
        
        if (assessments.includes("Significant")) {
          finalAssessment = "Significant";
        } else if (assessments.includes("Higher")) {
          finalAssessment = "Higher";
        } else if (assessments.includes("Lower")) {
          finalAssessment = "Lower";
        } else if (assessments.includes("NRPMM")) {
          finalAssessment = "NRPMM";
        }

        setRiskOfMaterialMisstatement(finalAssessment);
        
        // Only update if the value actually changed to prevent unnecessary re-renders
        // Use dataRef to check current value without causing dependency issues
        if (dataRef.current.riskOfMaterialMisstatement !== finalAssessment) {
          onChangeRef.current({ riskOfMaterialMisstatement: finalAssessment });
        }
      } catch (err) {
        console.error("Failed to load risk assessment:", err);
        setRiskOfMaterialMisstatement("No Risks Associated");
      } finally {
        isLoadingRef.current = false;
        setLoadingRiskAssessment(false);
      }
    };

    loadRiskAssessment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlId]); // Only depend on controlId, not onChange
  const addOperatingStep = () => {
    const newStep = {
      id: `op-step-${Date.now()}`,
      stepDescription: "",
      natureOfProcedure: "",
      procedureDescription: "",
    };
    onChange({ operatingSteps: [...data.operatingSteps, newStep] });
  };

  const removeOperatingStep = useCallback((id: string) => {
    onChangeRef.current({ operatingSteps: dataRef.current.operatingSteps.filter((s) => s.id !== id) });
  }, []);

  const updateOperatingStep = useCallback((id: string, field: string, value: string) => {
    onChangeRef.current({
      operatingSteps: dataRef.current.operatingSteps.map((s) =>
        s.id === id ? { ...s, [field]: value } : s
      ),
    });
  }, []);

  const copyFromDesignImplementation = () => {
    const copiedSteps = data.designImplementationSteps.map((step, index) => ({
      id: `op-copied-${Date.now()}-${index}`,
      stepDescription: step.stepDescription,
      natureOfProcedure: step.natureOfProcedure,
      procedureDescription: step.procedureDescription,
    }));
    onChange({ operatingSteps: [...data.operatingSteps, ...copiedSteps] });
  };

  // Generate steps from Control Summary if detailed description is enabled
  const controlSteps = React.useMemo(() => 
    data.detailedDescriptionEnabled ? data.steps : [], 
    [data.detailedDescriptionEnabled, data.steps]
  );

  // Memoize operating steps lookups to prevent unnecessary re-renders
  const operatingStepsMap = React.useMemo(() => {
    const map = new Map<string, typeof data.operatingSteps[0]>();
    data.operatingSteps.forEach(step => {
      map.set(step.id, step);
    });
    return map;
  }, [data.operatingSteps]);

  // Memoize SelectItem lists to prevent recreation on every render
  const natureOfProcedureItems = React.useMemo(() => 
    NATURE_OF_PROCEDURE_OPTIONS.map((option) => (
      <SelectItem key={option} value={option}>
        {option}
      </SelectItem>
    )),
    []
  );

  const frequencyItems = React.useMemo(() =>
    FREQUENCY_OPTIONS.map((option) => (
      <SelectItem key={option} value={option}>
        {option}
      </SelectItem>
    )),
    []
  );

  // Memoize Select values to prevent unnecessary re-renders
  const timingOfProcedureValue = React.useMemo(() => data.timingOfProcedure || "Apportion", [data.timingOfProcedure]);
  const frequencyOfPerformanceValue = React.useMemo(() => data.frequencyOfPerformance || "", [data.frequencyOfPerformance]);
  const planningForDeviationsValue = React.useMemo(() => data.planningForDeviations ? "Yes" : "No", [data.planningForDeviations]);
  const isOperatingEffectiveValue = React.useMemo(() => data.isOperatingEffective || "Not Yet Concluded", [data.isOperatingEffective]);
  const defaultNatureValue = React.useMemo(() => data.operatingSteps[0]?.natureOfProcedure || "", [data.operatingSteps]);

  // Memoize Select callbacks to prevent infinite re-renders - use refs to avoid dependencies
  const handleTimingOfProcedureChange = useCallback((value: "Apportion" | "Conclude at Interim") => {
    onChangeRef.current({ timingOfProcedure: value });
  }, []);

  const handleFrequencyOfPerformanceChange = useCallback((value: string) => {
    onChangeRef.current({ frequencyOfPerformance: value });
  }, []);

  const handlePlanningForDeviationsChange = useCallback((value: string) => {
    onChangeRef.current({ planningForDeviations: value === "Yes" });
  }, []);

  const handleIsOperatingEffectiveChange = useCallback((value: "Yes" | "No" | "Not Yet Concluded") => {
    onChangeRef.current({ isOperatingEffective: value });
  }, []);

  // Helper function for handling nature of procedure change in control steps
  const handleControlStepNatureChange = useCallback((stepId: string, value: string) => {
    const currentData = dataRef.current;
    const existingStep = currentData.operatingSteps.find((s) => s.id === `op-${stepId}`);
    if (existingStep) {
      updateOperatingStep(`op-${stepId}`, "natureOfProcedure", value);
    } else {
      const step = currentData.steps.find((s) => s.id === stepId);
      onChangeRef.current({
        operatingSteps: [
          ...currentData.operatingSteps,
          {
            id: `op-${stepId}`,
            stepDescription: step?.content || "",
            natureOfProcedure: value,
            procedureDescription: "",
          },
        ],
      });
    }
  }, [updateOperatingStep]);

  // Helper function for handling default nature of procedure change
  const handleDefaultNatureChange = useCallback((value: string) => {
    const currentData = dataRef.current;
    if (currentData.operatingSteps.length > 0) {
      updateOperatingStep(currentData.operatingSteps[0].id, "natureOfProcedure", value);
    } else {
      onChangeRef.current({
        operatingSteps: [
          {
            id: `op-default-${Date.now()}`,
            stepDescription: "",
            natureOfProcedure: value,
            procedureDescription: "",
          },
        ],
      });
    }
  }, [updateOperatingStep]);

  // Helper function for handling control step procedure description change
  const handleControlStepProcedureChange = useCallback((stepId: string, content: string) => {
    const currentData = dataRef.current;
    const step = currentData.steps.find((s) => s.id === stepId);
    const existingStep = currentData.operatingSteps.find((s) => s.id === `op-${stepId}`);
    if (existingStep) {
      updateOperatingStep(`op-${stepId}`, "procedureDescription", content);
    } else {
      onChangeRef.current({
        operatingSteps: [
          ...currentData.operatingSteps,
          {
            id: `op-${stepId}`,
            stepDescription: step?.content || "",
            natureOfProcedure: "",
            procedureDescription: content,
          },
        ],
      });
    }
  }, [updateOperatingStep]);

  // Helper function for handling default procedure description change
  const handleDefaultProcedureChange = useCallback((content: string) => {
    const currentData = dataRef.current;
    if (currentData.operatingSteps.length > 0) {
      updateOperatingStep(currentData.operatingSteps[0].id, "procedureDescription", content);
    } else {
      onChangeRef.current({
        operatingSteps: [
          {
            id: `op-default-${Date.now()}`,
            stepDescription: "",
            natureOfProcedure: "",
            procedureDescription: content,
          },
        ],
      });
    }
  }, [updateOperatingStep]);

  return (
    <div className="bg-white/5 border border-white/10 rounded-lg">
      <div className="border-b border-white/10 p-6">
        <h2 className="text-xl font-semibold text-white">Operating Effectiveness</h2>
      </div>
      <div className="p-6 space-y-8">
        {/* Section 1: Timing & Extent of procedures */}
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-white">i. Timing & Extent of procedures</h3>

          {/* Timing of Procedure */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Label className="flex-1 text-white">What is the timing of procedure?</Label>
              <Select
                key="timing-of-procedure-select"
                value={timingOfProcedureValue}
                onValueChange={handleTimingOfProcedureChange}
                searchable={false}
              >
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/10" searchable={false}>
                  <SelectItem value="Apportion">Apportion</SelectItem>
                  <SelectItem value="Conclude at Interim">Conclude at Interim</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="p-4 border border-white/10 rounded-lg bg-black/40">
              {data.timingOfProcedure === "Apportion" ? (
                <p className="text-sm text-white/60">
                  Apportion the control test over the year, i.e., spread the total number of
                  selections throughout the year.
                </p>
              ) : (
                <p className="text-sm text-white/60">
                  Perform a complete test of the control at an interim date. This approach requires
                  us to perform sufficient testing to enable us to reach a preliminary conclusion
                  regarding the operating effectiveness of controls tested at interim date.
                </p>
              )}
            </div>

            <ControlDocumentManager
              controlId={controlId || data.controlId}
              workspaceLinked={data.workspaceLinked}
              documents={data.timingDocuments || []}
              onDocumentsChange={handleTimingDocumentsChange}
              textResponse={data.timingTextResponse}
              onTextResponseChange={handleTimingTextResponseChange}
              label="Timing of Procedure"
            />
          </div>

          {/* Extent of Procedures */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Label className="flex-1 text-white">Extent of Procedures - Document the frequency of performance of control:</Label>
              <Select
                key="frequency-of-performance-select"
                value={frequencyOfPerformanceValue}
                onValueChange={handleFrequencyOfPerformanceChange}
                searchable={false}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/10" searchable={false}>
                  {frequencyItems}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Assessment Table */}
          <div className="border border-white/10 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-black/40">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                    Particulars
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-white/60">
                    Assessment
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-white/10">
                  <td className="px-4 py-3 text-white">Frequency</td>
                  <td className="px-4 py-3 text-white">
                    {data.frequencyOfPerformance || "Not set"}
                  </td>
                </tr>
                <tr className="border-t border-white/10">
                  <td className="px-4 py-3 text-white">Risk of Material Misstatement</td>
                  <td className="px-4 py-3">
                    {loadingRiskAssessment ? (
                      <span className="text-white/60 text-sm">Loading...</span>
                    ) : (
                      <span className="text-white font-medium">
                        {riskOfMaterialMisstatement || data.riskOfMaterialMisstatement || "No Risks Associated"}
                      </span>
                    )}
                  </td>
                </tr>
                <tr className="border-t border-white/10">
                  <td className="px-4 py-3 text-white">Risk Associated with Control</td>
                  <td className="px-4 py-3 text-white">{data.riskAssociated}</td>
                </tr>
                <tr className="border-t border-white/10">
                  <td className="px-4 py-3 text-white">Sample Size</td>
                  <td className="px-4 py-3">
                    <Input
                      value={data.sampleSize}
                      onChange={(e) => onChange({ sampleSize: e.target.value })}
                      className="bg-black/20 border-white/10 text-white"
                      placeholder="Enter sample size"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Planning for Deviations */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <Label className="flex-1 text-white">
                Are we planning for deviations in our tests of operating effectiveness?
              </Label>
              <Select
                key="planning-for-deviations-select"
                value={planningForDeviationsValue}
                onValueChange={handlePlanningForDeviationsChange}
                searchable={false}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/10" searchable={false}>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {data.planningForDeviations && (
              <div className="space-y-4 pl-4 border-l-2 border-primary">
                <RichTextEditor
                  label="Document what constitutes a deviation in the control:"
                  value={data.deviationDescription}
                  onChange={(content) => onChange({ deviationDescription: content })}
                  placeholder="Describe what constitutes a deviation..."
                />
                <RichTextEditor
                  label="Document rationale behind planning for deviation in test of operating effectiveness:"
                  value={data.deviationRationale}
                  onChange={(content) => onChange({ deviationRationale: content })}
                  placeholder="Describe the rationale..."
                />
              </div>
            )}
          </div>
        </div>

        {/* Section 2: Nature of Procedures */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">ii. Nature of Procedures</h3>

          {/* Copy from Design & Implementation button */}
          {data.designImplementationSteps.length > 0 && (
            <Button
              variant="outline"
              onClick={copyFromDesignImplementation}
              className="border-white/10 text-white hover:bg-white/10"
            >
              <Copy className="h-4 w-4 mr-2" />
              Copy procedures from Design and Implementation
            </Button>
          )}

          {/* Steps from detailed description OR single procedure block */}
          {controlSteps.length > 0 ? (
            <div className="space-y-4">
              {controlSteps.map((step, index) => (
                <div
                  key={step.id}
                  className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4"
                >
                  <h4 className="text-sm font-semibold text-white">Step {index + 1}</h4>
                  <div
                    className="text-sm text-white/60 mb-4 p-3 bg-black/40 rounded-lg"
                    dangerouslySetInnerHTML={{ __html: step.content }}
                  />

                  <div className="space-y-4">
                    <h5 className="text-sm font-medium text-white">
                      Procedures performed to evaluate the operating effectiveness of control:
                    </h5>

                    <div className="space-y-2">
                      <Label className="text-white">Nature of testing procedure</Label>
                      <Select
                        key={`nature-select-${step.id}`}
                        value={
                          operatingStepsMap.get(`op-${step.id}`)
                            ?.natureOfProcedure || ""
                        }
                        onValueChange={(value: string) => handleControlStepNatureChange(step.id, value)}
                        searchable={false}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select nature of procedure" />
                        </SelectTrigger>
                        <SelectContent className="bg-black/90 border-white/10" searchable={false}>
                          {natureOfProcedureItems}
                        </SelectContent>
                      </Select>
                    </div>

                    <RichTextEditor
                      label="Procedure Description"
                      value={
                        data.operatingSteps.find((s) => s.id === `op-${step.id}`)
                          ?.procedureDescription || ""
                      }
                      onChange={(content) => handleControlStepProcedureChange(step.id, content)}
                      placeholder="Describe the procedure..."
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Single procedure block when detailed description is off */}
              <div className="p-4 border border-white/10 rounded-lg bg-black/20 space-y-4">
                <h5 className="text-sm font-medium text-white">
                  Procedures performed to evaluate the operating effectiveness of control:
                </h5>

                <div className="space-y-2">
                  <Label className="text-white">Nature of testing procedure</Label>
                  <Select
                    key="default-nature-select"
                    value={defaultNatureValue}
                    onValueChange={handleDefaultNatureChange}
                    searchable={false}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select nature of procedure" />
                    </SelectTrigger>
                    <SelectContent className="bg-black/90 border-white/10" searchable={false}>
                      {natureOfProcedureItems}
                    </SelectContent>
                  </Select>
                </div>

                <RichTextEditor
                  label="Procedure Description"
                  value={data.operatingSteps[0]?.procedureDescription || ""}
                  onChange={handleDefaultProcedureChange}
                  placeholder="Describe the procedure..."
                />
              </div>

              {/* Additional procedures */}
              {data.operatingSteps.slice(1).map((step, index) => (
                <OperatingStepItem
                  key={step.id}
                  step={step}
                  index={index}
                  onUpdate={updateOperatingStep}
                  onRemove={removeOperatingStep}
                />
              ))}

              <Button variant="outline" onClick={addOperatingStep} className="border-white/10 text-white hover:bg-white/10">
                <Plus className="h-4 w-4 mr-2" />
                Add Procedure
              </Button>
            </div>
          )}

          {/* Perform tests */}
          <div className="space-y-3 border-t border-white/10 pt-6">
            <h4 className="text-sm font-semibold text-white">
              Perform tests of operating effectiveness
            </h4>
            <p className="text-sm text-white/60">
              For each activity, we document the procedures performed to test the operating
              effectiveness.
            </p>
            <ControlDocumentManager
              controlId={controlId || data.controlId}
              workspaceLinked={data.workspaceLinked}
              documents={data.operatingTestsDocuments || []}
              onDocumentsChange={handleOperatingTestsDocumentsChange}
              textResponse={data.operatingTestsTextResponse}
              onTextResponseChange={handleOperatingTestsTextResponseChange}
              label="Operating Tests"
            />
          </div>
        </div>

        {/* Section 3: Operating Effectiveness Testing Conclusion */}
        <div className="space-y-6 border-t border-white/10 pt-6">
          <h3 className="text-lg font-medium text-white">
            iii. Operating Effectiveness Testing Conclusion
          </h3>

          <div className="flex items-center gap-4">
            <Label className="flex-1 text-white">Is the control operating effectively?</Label>
            <Select
              key="is-operating-effective-select"
              value={isOperatingEffectiveValue}
              onValueChange={handleIsOperatingEffectiveChange}
              searchable={false}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-black/90 border-white/10" searchable={false}>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="No">No</SelectItem>
                <SelectItem value="Not Yet Concluded">Not Yet Concluded</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
});

